import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { verifyTwoFA, updateTwofa, forgotTwofaPin, validateTwofaOtp } from '../../services/backendAPI';

const GhostPinInput = ({ value, onChange, showPin, label, disabled, isError, autoFocus }) => {
  const theme = useTheme();
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
      {label && <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>{label}</Typography>}
      <Box sx={{ position: 'relative', width: '260px', height: '48px' }}>
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          value={value}
          onFocus={(e) => {
            setIsFocused(true);
            const len = e.target.value.length;
            // Force cursor to the end of the text
            setTimeout(() => e.target.setSelectionRange(len, len), 0);
          }}
          onBlur={() => setIsFocused(false)}
          onClick={(e) => {
            const len = e.target.value.length;
            // Force cursor to the end of the text if they tap anywhere
            e.target.setSelectionRange(len, len);
          }}
          onKeyDown={(e) => {
            // Prevent arrow keys from moving the cursor
            if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
              e.preventDefault();
            }
          }}
          onChange={(e) => {
            const val = e.target.value.replace(/[^0-9]/g, '');
            onChange(val.slice(0, 6));
          }}
          disabled={disabled}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: 0, // Completely invisible ghost input
            zIndex: 10,
            cursor: disabled ? 'not-allowed' : 'pointer'
          }}
          autoComplete="off"
        />
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none'
        }}>
          {[...Array(6)].map((_, i) => {
            const hasValue = i < value.length;
            const char = hasValue ? value[i] : '';
            const isActive = isFocused && (i === value.length || (value.length === 6 && i === 5));
            
            return (
              <Box key={i} sx={{
                width: '36px',
                height: '48px',
                borderBottom: `3px solid ${isError ? theme.palette.error.main : (isActive ? theme.palette.primary.main : (hasValue ? theme.palette.text.primary : theme.palette.divider))}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'border-color 0.2s',
                position: 'relative',
              }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 'bold',
                  WebkitTextSecurity: (!showPin && hasValue) ? 'disc' : 'none'
                }}>
                  {char}
                </Typography>

                {/* Simulated Blinking Cursor */}
                {isActive && !hasValue && (
                  <Box sx={{
                    position: 'absolute',
                    width: '2px',
                    height: '24px',
                    backgroundColor: theme.palette.primary.main,
                    animation: 'blinker 1s step-start infinite',
                    '@keyframes blinker': {
                      '50%': { opacity: 0 }
                    }
                  }} />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

const TwoFactorAuth = ({ onVerify }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showPin, setShowPin] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Unified State Machine
  const [mode, setMode] = useState(() => {
    if (localStorage.getItem('2fa_setup_required') === 'true') return 'SETUP';
    return 'LOGIN';
  });
  
  // Input Values
  const [pinValue, setPinValue] = useState(''); // Used for standard login
  const [otpValue, setOtpValue] = useState('');
  const [newPinValue, setNewPinValue] = useState('');
  const [confirmPinValue, setConfirmPinValue] = useState('');
  
  const [referenceToken, setReferenceToken] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  const [error, setError] = useState(() => {
    const expiry = localStorage.getItem('2fa_lockout_expiry');
    if (expiry && Number(expiry) > Date.now()) {
      return 'Exceeded 2FA limit. Please try after some time.';
    }
    return '';
  });
  const [countdown, setCountdown] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [lockoutTimeLeft, setLockoutTimeLeft] = useState(() => {
    const expiry = localStorage.getItem('2fa_lockout_expiry');
    if (expiry) {
      const remaining = Math.round((Number(expiry) - Date.now()) / 1000);
      return remaining > 0 ? remaining : 0;
    }
    return 0;
  });

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  useEffect(() => {
    if (countdown !== null) {
      if (countdown > 0) {
        const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        return () => clearTimeout(timer);
      } else {
        handleBackToLogin();
      }
    }
  }, [countdown]);

  useEffect(() => {
    if (lockoutTimeLeft > 0) {
      const timer = setTimeout(() => setLockoutTimeLeft(lockoutTimeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      localStorage.removeItem('2fa_lockout_expiry');
    }
  }, [lockoutTimeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startLockout = (seconds) => {
    const expiry = Date.now() + seconds * 1000;
    localStorage.setItem('2fa_lockout_expiry', expiry.toString());
    setLockoutTimeLeft(seconds);
  };

  const handleError = (message) => {
    setError(message);

    if (
      message.toLowerCase().includes('0 attempts remaining') ||
      message.toLowerCase().includes('session expired') ||
      message.toLowerCase().includes('relogin')
    ) {
      setRedirecting(true);
      setCountdown(3);
    }
  };

  const handleForgotPinClick = async () => {
    setIsSubmitting(true);
    try {
      const loginId = localStorage.getItem('login_id');
      const response = await forgotTwofaPin(loginId);
      if (response.status === 'success') {
        setReferenceToken(response.data.reference_token);
        setMode('FORGOT');
        setResendTimer(60);
        setLockoutTimeLeft(0);
        localStorage.removeItem('2fa_lockout_expiry');
        setError('');
        setPinValue('');
      } else {
        setError(response.message || 'Failed to initiate forgot PIN request');
      }
    } catch (err) {
      setError(err?.message || 'Failed to initiate forgot PIN request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    setIsSubmitting(true);
    try {
      const loginId = localStorage.getItem('login_id');
      const response = await forgotTwofaPin(loginId);
      if (response.status === 'success') {
        setReferenceToken(response.data.reference_token);
        setResendTimer(60);
        setOtpValue('');
        setError('');
      } else {
        setError(response.message || 'Failed to resend OTP');
      }
    } catch (err) {
      setError(err?.message || 'Failed to resend OTP');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    const keysToRemove = [
      'isAuthenticated', '2faVerified', 'login_id', 'auth_token', 
      'login_name', 'dashboardNotificationClosed', 'dashboardNotificationIndex',
      'twofa_token', 'twofa_questions', 'twofa_type', '2fa_lockout_expiry', 
      '2fa_setup_required', 'token'
    ];
    keysToRemove.forEach(key => localStorage.removeItem(key));
    sessionStorage.removeItem('zoho_autoopen_support');
    window.location.replace('/login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lockoutTimeLeft > 0) return;
    setError('');

    const loginId = localStorage.getItem('login_id');

    if (mode === 'LOGIN') {
      if (pinValue.length !== 6) return setError('Please enter a 6-digit PIN');
      setIsSubmitting(true);
      try {
        const twofaToken = localStorage.getItem('twofa_token');
        const twofaType = localStorage.getItem('twofa_type') || 'PIN';
        const questions = JSON.parse(localStorage.getItem('twofa_questions') || '[]');
        
        const response = await verifyTwoFA({
          loginId, pin: pinValue, twofaToken, twofaType, questions
        });
        
        if (response.status === 'success') {
          localStorage.setItem('auth_token', response.data.auth_token);
          localStorage.setItem('2faVerified', 'true');
          
          // Clean up temp tokens
          localStorage.removeItem('twofa_token');
          localStorage.removeItem('twofa_questions');
          localStorage.removeItem('twofa_type');
          localStorage.removeItem('2fa_lockout_expiry');
          
          onVerify();
        } else {
          if (response.time_left) {
            startLockout(response.time_left);
          }
          handleError(response.message || 'Invalid PIN');
          setPinValue(''); // Clear wrong PIN for retry
        }
      } catch (err) {
        if (err?.data?.time_left) startLockout(Number(err.data.time_left));
        handleError(err?.message || 'Verification failed');
      } finally {
        setIsSubmitting(false);
      }
    } 
    else if (mode === 'SETUP') {
      if (newPinValue.length !== 6 || confirmPinValue.length !== 6) return setError('Please enter a full 6-digit PIN');
      if (newPinValue !== confirmPinValue) return setError('PINs do not match. Please try again.');
      setIsSubmitting(true);
      try {
        const response = await updateTwofa('PIN', newPinValue);
        if (response.status === 'success') {
          if (response.data?.auth_token) localStorage.setItem('auth_token', response.data.auth_token);
          localStorage.setItem('2faVerified', 'true');
          localStorage.removeItem('2fa_setup_required');
          onVerify();
        } else {
          handleError(response.message || 'Failed to setup PIN');
        }
      } catch (err) {
        handleError(err?.message || 'Failed to setup PIN');
      } finally {
        setIsSubmitting(false);
      }
    }
    else if (mode === 'FORGOT') {
      if (otpValue.length !== 6) return setError('Please enter the 6-digit OTP');
      if (newPinValue.length !== 6 || confirmPinValue.length !== 6) return setError('Please enter a full 6-digit PIN');
      if (newPinValue !== confirmPinValue) return setError('PINs do not match. Please try again.');
      
      setIsSubmitting(true);
      try {
        // Step 1: Validate OTP
        const otpResponse = await validateTwofaOtp(referenceToken, otpValue);
        if (otpResponse.status !== 'success') {
          handleError(otpResponse.message || 'Invalid OTP');
          setOtpValue(''); // Only clear the OTP, preserve their newly typed PINs
          setIsSubmitting(false);
          return;
        }

        // Save the auth token granted by the OTP success so updateTwofa can use it
        if (otpResponse.data?.auth_token) {
          localStorage.setItem('auth_token', otpResponse.data.auth_token);
        }

        // Step 2: Instantly update PIN using the auth_token
        const updateResponse = await updateTwofa('PIN', newPinValue);
        if (updateResponse.status === 'success') {
          if (updateResponse.data?.auth_token) localStorage.setItem('auth_token', updateResponse.data.auth_token);
          localStorage.setItem('2faVerified', 'true');
          localStorage.removeItem('2fa_setup_required');
          onVerify();
        } else {
          handleError(updateResponse.message || 'Failed to update PIN');
        }
      } catch (err) {
        handleError(err?.message || 'Failed to reset PIN');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        background: theme.palette.mode === 'dark' 
          ? `linear-gradient(45deg, ${alpha(theme.palette.primary.dark, 0.1)}, ${alpha(theme.palette.background.default, 0.95)})`
          : `linear-gradient(45deg, ${alpha(theme.palette.primary.light, 0.1)}, ${alpha(theme.palette.background.default, 0.95)})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflowY: 'auto'
      }}
    >
      <Paper
        elevation={isMobile ? 0 : 4}
        sx={{
          width: '100%',
          maxWidth: isMobile ? '100%' : '440px',
          height: isMobile ? '100vh' : 'auto', // Changed minHeight back to height
          mx: isMobile ? 0 : 3,
          my: isMobile ? 0 : 4,
          borderRadius: isMobile ? 0 : '16px',
          background: theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header Section */}
        <Box sx={{ 
          px: 4,
          pt: isMobile ? 6 : 4,
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}>
          <Typography variant="h5" align="center" sx={{ fontWeight: 600, mb: 1 }}>
            {mode === 'LOGIN' && 'Enter PIN'}
            {mode === 'SETUP' && 'Create PIN'}
            {mode === 'FORGOT' && 'Reset PIN'}
          </Typography>
          <Typography variant="body2" color="textSecondary" align="center">
            {mode === 'LOGIN' && 'Please enter your 6-digit security PIN'}
            {mode === 'SETUP' && 'Create a new 6-digit PIN to secure your account'}
            {mode === 'FORGOT' && 'Enter the OTP sent to you and choose a new PIN'}
          </Typography>
        </Box>

        {/* Form Section */}
        <Box 
          component="form" 
          onSubmit={handleSubmit} 
          sx={{ 
            display: 'flex',
            flexDirection: 'column',
            p: 4,
            flex: 1,
            ...(isMobile && {
              height: 'calc(100vh - 140px)', // Increased subtraction to move content up
              justifyContent: 'space-between'
            })
          }}
        >
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mt: 2
          }}>
            {/* Conditional Ghost Inputs based on Mode */}
            {mode === 'FORGOT' && (
              <GhostPinInput 
                autoFocus={true}
                label="Enter OTP"
                value={otpValue} 
                onChange={setOtpValue} 
                showPin={showPin} 
                disabled={lockoutTimeLeft > 0 || isSubmitting} 
                isError={!!error && otpValue.length < 6}
              />
            )}

            {(mode === 'SETUP' || mode === 'FORGOT') && (
              <>
                <GhostPinInput 
                  autoFocus={mode === 'SETUP'}
                  label="New PIN"
                  value={newPinValue} 
                  onChange={setNewPinValue} 
                  showPin={showPin} 
                  disabled={lockoutTimeLeft > 0 || isSubmitting} 
                  isError={!!error && (newPinValue.length < 6 || error.toLowerCase().includes('match'))}
                />
                <GhostPinInput 
                  label="Confirm New PIN"
                  value={confirmPinValue} 
                  onChange={setConfirmPinValue} 
                  showPin={showPin} 
                  disabled={lockoutTimeLeft > 0 || isSubmitting} 
                  isError={!!error && (confirmPinValue.length < 6 || error.toLowerCase().includes('match'))}
                />
              </>
            )}

            {mode === 'LOGIN' && (
              <GhostPinInput 
                autoFocus={true}
                value={pinValue} 
                onChange={setPinValue} 
                showPin={showPin} 
                disabled={lockoutTimeLeft > 0 || isSubmitting} 
                isError={!!error}
              />
            )}

            <Box sx={{ width: '100%', maxWidth: '260px', display: 'flex', justifyContent: 'flex-end', mt: -1, mb: 1 }}>
              <Button 
                size="small"
                onClick={() => setShowPin(!showPin)} 
                startIcon={showPin ? <VisibilityOff /> : <Visibility />}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
              >
                {showPin ? 'Hide' : 'Show'}
              </Button>
            </Box>

            {error && (
              <Box sx={{ 
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: 'error.main', mt: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center' }}>
                  <LockIcon sx={{ fontSize: '1rem', flexShrink: 0 }} />
                  <Typography variant="body2" align="center" sx={{ fontWeight: 500 }}>
                    {error}
                  </Typography>
                </Box>
                {lockoutTimeLeft > 0 && (
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                    Time left - {formatTime(lockoutTimeLeft)} minutes
                  </Typography>
                )}
                {redirecting && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} color="inherit" />
                    <Typography variant="body2">
                      Redirecting to login in {countdown}s...
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Actions Section */}
          <Box sx={{ 
            width: '100%',
            ...(isMobile ? {
              mb: 4
            } : {
              mt: 4
            })
          }}>
            <Button
              type="submit"
              disabled={lockoutTimeLeft > 0 || isSubmitting}
              fullWidth
              variant="contained"
              sx={{ 
                py: 1.5,
                fontSize: '0.9375rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
                height: '48px'
              }}
            >
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Confirm'}
            </Button>
            
            {mode === 'LOGIN' && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button 
                  variant="text" 
                  onClick={handleForgotPinClick}
                  disabled={isSubmitting}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Forgot PIN?
                </Button>
              </Box>
            )}
            
            {mode === 'FORGOT' && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button 
                  variant="text" 
                  onClick={handleResendOtp}
                  disabled={resendTimer > 0 || isSubmitting}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                </Button>
              </Box>
            )}

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button 
                variant="text" 
                onClick={handleBackToLogin}
                sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
              >
                Back to login page
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default TwoFactorAuth;
